import React, { useEffect } from 'react';
import { GetPersonUser, PersonBenefits } from '@esportal/portal-service';
import { DataVehicles } from '@esportal/portal-service';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import { TokenTextAppearance, Text } from '@volkswagen-onehub/components-core';
import { StyledProgressContainer, Message, StyledProgress } from './ui/loading-manager-styles';
import { ErrorComponent } from './error-component';
import { PortalContext } from '../../context/portal-context';
import { useUniversalEffect } from '@volkswagen-onehub/vw-starter-feature-app-support';

export const LoadingManager = ({
	userVehicles,
	userData,
	personBenefits,
	hasError,
	validatingIDKit,
	isLoading,
}: {
	userVehicles: DataVehicles | null;
	userData: GetPersonUser | null;
	personBenefits?: PersonBenefits | null;
	hasError: boolean;
	validatingIDKit: boolean;
	isLoading: boolean;
}): React.ReactElement => {
	const trackingManager = useTrackingManager();
	const [progressValue, setProgressValue] = React.useState<number>(0.15);
	const [showAlmostThere, setShowAlmostThere] = React.useState<boolean>(false);
	const { pageInfoService } = React.useContext(PortalContext);

	useUniversalEffect(() => {
		if (!hasError) return;

		pageInfoService.setPageInfo({
			seo: { pageTitle: 'Club Volkswagen - Error' },
		});
	}, [hasError, pageInfoService]);

	useEffect(() => {
		let target = progressValue;

		if (!validatingIDKit) {
			target = 0.5;
		}
		if (userData && !userVehicles) {
			target = 0.75;
		}
		if (userData && userVehicles) {
			target = 0.9;
		}
		if (userData && userVehicles && personBenefits) {
			target = 1;
		}

		const step = () => {
			setProgressValue((prev) => {
				const diff = target - prev;
				const next = Math.abs(diff) < 0.01 ? target : prev + diff * 0.1;

				if (next >= 0.75 && !showAlmostThere) {
					setShowAlmostThere(true);
				}

				return next;
			});
		};

		const interval = setInterval(step, 1000 / 60); // 60fps
		return () => clearInterval(interval);
	}, [validatingIDKit, userData, userVehicles, personBenefits, showAlmostThere]);

	useEffect(() => {
		if (hasError) {
			let errorType = '';

			if (!userVehicles && userData && !validatingIDKit) {
				errorType = 'vehicles';
			} else if (!userData && !validatingIDKit) {
				errorType = 'person';
			} else if (!validatingIDKit) {
				errorType = 'user';
			}
			trackingManager.trackClubVWErrorMessageLoad(
				{
					message: 'Uy, algo no está funcionando.',
				},
				{
					contentId: `Error: AuthProxy - ${errorType}`,
				},
			);
		}
	}, [hasError]);

	return (
		<>
			{hasError ? (
				<ErrorComponent />
			) : isLoading ? (
				<StyledProgressContainer>
					<StyledProgress value={progressValue} />
					{showAlmostThere && (
						<Message>
							<Text appearance={TokenTextAppearance.headline300} bold>
								Casi lo tenemos...
							</Text>
						</Message>
					)}
				</StyledProgressContainer>
			) : null}
		</>
	);
};
